<template>
  <vue-good-table
    v-if="!isMobile"
    :columns="columns"
    :rows="rows"
    :row-style-class="rowStyleClass"
    :sort-options="sortOptions"
    styleClass="table table-striped"
    mode="remote"
    @on-row-click="($event) => $emit('on-row-click', $event)"
    @on-sort-change="onSortChange">
    <template v-slot:table-row="props">
      <Cell :data="props"/>
    </template>
    <template v-slot:emptystate>
      <div class="flex flex-row items-center justify-center">
        <span
          v-if="busy === false"
          class="text-lg font-semibold">
          Ничего не найдено
        </span>
      </div>
    </template>
  </vue-good-table>

  <div v-else class="mobile-data-table">
    <div
      v-if="sortableColumns.length > 0 && rows.length > 0"
      class="mobile-data-table__toolbar">
      <select
        v-model="mobileSortField"
        class="mobile-data-table__sort"
        aria-label="Сортировка"
        @change="applyMobileSort">
        <option :value="null" disabled>Сортировка</option>
        <option
          v-for="column in sortableColumns"
          :key="column.field"
          :value="column.field">
          Сортировать: {{ column.label }}
        </option>
      </select>
      <button
        type="button"
        class="mobile-data-table__direction"
        :disabled="!mobileSortField"
        :aria-label="mobileSortDescending ? 'По убыванию' : 'По возрастанию'"
        @click="toggleMobileSortDirection">
        {{ mobileSortDescending ? '↓' : '↑' }}
      </button>
    </div>

    <template v-if="rows.length > 0">
      <article
        v-for="(row, rowIndex) in rows"
        :key="rowKey(row, rowIndex)"
        class="mobile-table-card"
        :class="[mobileRowClass(row), { 'is-clickable': rowClickable }]"
        :role="rowClickable ? 'button' : null"
        :tabindex="rowClickable ? 0 : null"
        @click="onMobileRowClick(row, $event)"
        @keyup.enter="onMobileRowClick(row, $event)">
        <div class="mobile-table-card__grid">
          <div
            v-for="column in mobileColumns"
            :key="column.field || column.label"
            class="mobile-table-card__field">
            <span class="mobile-table-card__label">{{ column.label }}</span>
            <div class="mobile-table-card__value">
              <Cell :data="mobileCellData(row, column, rowIndex)"/>
            </div>
          </div>
        </div>
      </article>
    </template>

    <div
      v-else-if="busy === false"
      class="mobile-data-table__empty">
      Ничего не найдено
    </div>
  </div>
</template>

<script>
import delve from 'dlv';
import Cell from '@/components/Table/Cell.vue';

export default {
  props: {
    columns: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      required: false,
      default: () => [],
    },
    rowStyleClass: {
      type: Function,
      required: false,
    },
    sortOptions: {
      type: Object,
      required: false,
      default: () => {},
    },
    busy: {
      validator: (value) => typeof value === 'boolean',
    },
    mobileColumnLimit: {
      type: Number,
      required: false,
      // 0 means: show every labelled desktop column on mobile.
      // A positive value remains available for an explicitly compact table.
      default: 0,
    },
  },
  components: {
    Cell,
  },
  data() {
    return {
      isMobile: typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(max-width: 767px)').matches,
      mobileMediaQuery: null,
      mobileSortField: null,
      mobileSortDescending: false,
    };
  },
  computed: {
    mobileColumns() {
      const visible = this.columns.filter((column) => column.mobileHidden !== true && column.label);
      const prioritized = visible
        .filter((column) => Number.isFinite(column.mobilePriority))
        .sort((a, b) => a.mobilePriority - b.mobilePriority);
      const regular = visible.filter((column) => !Number.isFinite(column.mobilePriority));
      const ordered = [...prioritized, ...regular];
      return this.mobileColumnLimit > 0
        ? ordered.slice(0, this.mobileColumnLimit)
        : ordered;
    },
    sortableColumns() {
      if (this.sortOptions && this.sortOptions.enabled === false) return [];
      return this.columns.filter((column) => column.sortable === true && column.field);
    },
    rowClickable() {
      return !!this.$listeners['on-row-click'];
    },
  },
  watch: {
    sortableColumns: {
      handler(columns) {
        if (!columns.length) {
          this.mobileSortField = null;
          return;
        }
        if (!columns.some((column) => column.field === this.mobileSortField)) {
          this.mobileSortField = null;
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mobileMediaQuery = window.matchMedia('(max-width: 767px)');
      this.isMobile = this.mobileMediaQuery.matches;
      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.onViewportChange);
      } else if (this.mobileMediaQuery.addListener) {
        this.mobileMediaQuery.addListener(this.onViewportChange);
      }
    }
  },
  beforeDestroy() {
    if (!this.mobileMediaQuery) return;
    if (this.mobileMediaQuery.removeEventListener) {
      this.mobileMediaQuery.removeEventListener('change', this.onViewportChange);
    } else if (this.mobileMediaQuery.removeListener) {
      this.mobileMediaQuery.removeListener(this.onViewportChange);
    }
  },
  methods: {
    onViewportChange(event) {
      this.isMobile = event.matches;
    },
    onSortChange(event) {
      this.$emit('on-sort-change', event.map((obj) => ({
        column: this.$voca.snakeCase(obj.field),
        descending: +(obj.type === 'desc'),
      })));
    },
    applyMobileSort() {
      if (!this.mobileSortField) return;
      this.$emit('on-sort-change', [{
        column: this.$voca.snakeCase(this.mobileSortField),
        descending: +this.mobileSortDescending,
      }]);
    },
    toggleMobileSortDirection() {
      this.mobileSortDescending = !this.mobileSortDescending;
      this.applyMobileSort();
    },
    mobileRowClass(row) {
      return this.rowStyleClass ? this.rowStyleClass(row) : '';
    },
    rowKey(row, index) {
      return row && typeof row.id !== 'undefined' ? `row-${row.id}` : `row-${index}`;
    },
    formattedRow(row) {
      return this.columns.reduce((output, column) => {
        if (!column.field) return output;
        return {
          ...output,
          [column.field]: delve(row, column.field),
        };
      }, {});
    },
    mobileCellData(row, column, rowIndex) {
      return {
        row,
        column,
        formattedRow: this.formattedRow(row),
        rowIndex,
      };
    },
    onMobileRowClick(row, originalEvent) {
      if (!this.rowClickable) return;
      const target = originalEvent && originalEvent.target;
      if (target && target.closest) {
        const interactive = target.closest('a, button, input, select, textarea, label, [role="button"]');
        if (interactive && interactive !== originalEvent.currentTarget) return;
      }
      // Keep the same event contract as vue-good-table on desktop.
      // `originalEvent` is retained for mobile-aware consumers.
      this.$emit('on-row-click', {
        row,
        event: originalEvent,
        originalEvent,
      });
    },
  },
};
</script>
